import { useState } from "react"
import MultiSelect from "../common/FormControls/MultiSelect"
import VSpace from "../common/VSpace"
import { Attachment, createBlankAttachment, filterAttachmentsByFolder, filterAttachmentsBySearch, filterAttachmentsByTags, filterForPreview, getFolderFilterOptions, getTagList } from "./attachmentsModel"
import { Flex, Input, Select, Space } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition, faClock, faEdit, faFolder, faTags } from "@fortawesome/pro-solid-svg-icons"
import TooltipIcon from "../TooltipIcon"
import EditAttachmentModal from "./EditAttachmentModal"
import "./AttachmentsViewer.css"
import { grey } from "@ant-design/colors"
import { faFile, faFileAlt, faFileArchive, faFileExcel, faFileImage, faFilePdf, faFileVideo, faFileWord } from "@fortawesome/pro-duotone-svg-icons"
import IconAndTextButton from "../IconAndTextButton"
import AttachmentsGallery from "./AttachmentsGallery"

export type Props = {
  attachments: Attachment[]
  showFolders: boolean
  customerId: number
  propertyId?: number
  showFolderFilter?: boolean
}

const iconMap: Record<string, IconDefinition> = {
  'fa-file-word': faFileWord,
  'fa-file-excel': faFileExcel,
  'fa-file-pdf': faFilePdf,
  'fa-file-archive': faFileArchive,
  'fa-file-image': faFileImage,
  'fa-file-video': faFileVideo,
  'fa-file-alt': faFileAlt
}

const AttachmentsViewer = ({ attachments, showFolders, customerId, propertyId, showFolderFilter }: Props) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [attachmentForEdit, setAttachmentForEdit] = useState<Attachment | undefined>()
  const [galleryIndex, setGalleryIndex] = useState<number | undefined>()
  const [selectedFolder, setSelectedFolder] = useState('')
  const folderFilterOptions = getFolderFilterOptions(attachments)

  const allTags = getTagList(attachments)
  const filteredAttachments = filterAttachmentsBySearch(filterAttachmentsByTags(filterAttachmentsByFolder(attachments, selectedFolder), selectedTags), search)

  const imageAttachments = filterForPreview(filteredAttachments)

  const handleSelect = (attachment: Attachment) => {
    const imageIndex = imageAttachments.indexOf(attachment)
    if (imageIndex >= 0) {
      setGalleryIndex(imageIndex)
      return
    }

    window.open(attachment.url, '_blank')
  }

  return <>
    <section>
      <VSpace>
        <Flex gap={10}>
          <Input.Search placeholder="Search" value={search} onChange={x => setSearch(x.target.value ?? '')} allowClear />
          {showFolderFilter && <Select value={selectedFolder} options={folderFilterOptions} onChange={x => {console.log(x); setSelectedFolder(x)}} style={{width: '100%'}} popupMatchSelectWidth={false} />}
        </Flex>
        <Flex align="center" gap={10}>
          <FontAwesomeIcon icon={faTags} />
          <TooltipIcon message="Attachments tagged as admin can only be viewed by users with the allow invoicing permission." />
          <MultiSelect
            options={allTags}
            selectedValues={selectedTags}
            onChange={setSelectedTags}
            maxTagCount={5}
            placeholder={"filter by tag"}
            style={{ width: '100%' }}
          />
        </Flex>
      </VSpace>
    </section>
    <section>
      {attachments.length === 0 && <Space>
        <span>No attachments found.</span>
      </Space>}
      {attachments.length > 0 && <ul className="react-attachments-list">
        {filteredAttachments.map(attachment => <li key={attachment.id} onClick={() => handleSelect(attachment)}>
            {attachment.thumbnail && <img className="attachment-thumbnail" src={attachment.thumbnail} />}
            {!attachment.thumbnail && <div className="attachment-icon"><FontAwesomeIcon icon={iconMap[attachment.icon] ?? faFile} /></div>}
            <span className="filename">{attachment.name}</span>
            <div className="details">
              {showFolders && <Space size="small">
                <FontAwesomeIcon color={grey[2]} icon={faFolder} />
                <span>{attachment.folder}</span>
              </Space>}
              <div>{attachment.size}</div>
              <div>
                <Space size="small">
                  <FontAwesomeIcon color={grey[2]} icon={faClock} />
                  <span>{attachment.dateTime}</span>
                </Space>
              </div>
              {attachment.tags?.length > 0 && <div>
                <Space size="small">
                  <FontAwesomeIcon color={grey[2]} icon={faTags} />
                  <span>{attachment.tags}</span>
                </Space>
              </div>}
            </div>
            <div className="edit-attachment">
              <IconAndTextButton icon={faEdit} text="Edit" onClick={(event) => {
                setAttachmentForEdit(attachment)
                event.stopPropagation()
              }} type="primary" />
            </div>
        </li>)}
      </ul>}
    </section>

    <EditAttachmentModal
      isOpen={attachmentForEdit !== undefined}
      onClose={() => setAttachmentForEdit(undefined)}
      attachment={attachmentForEdit ?? createBlankAttachment()}
      customerId={customerId}
      propertyId={propertyId}
    />

    {galleryIndex !== undefined && <AttachmentsGallery
      attachments={imageAttachments}
      selectedIndex={galleryIndex}
      onIndexChange={setGalleryIndex}
    />}
  </>
}

export default AttachmentsViewer
